import { useMemo } from 'react';
import { MultimodalPart } from 'data/firestore/globals';
import { ChatMessage } from './types';

// Type guard to check if content is an array of MultimodalPart
const isMultimodalContent = (
    content: string | Array<MultimodalPart> | undefined,
): content is Array<MultimodalPart> => Array.isArray(content);

interface MessageRenderingMetadata {
    isMessageSent: boolean;
    hasMultimodalContent: boolean;
    multimodalParts: Array<MultimodalPart>;
    hasText: boolean;
    hasMedia: boolean;
    validMedia: any[];
    totalBubbles: number;
}

const useMessageRenderingMetadata = (message: ChatMessage): MessageRenderingMetadata => {
    // Check if message-sent has both content and media
    const isMessageSent = message.type === 'message-sent';
    const messageContent = isMessageSent ? message.content : null;

    // Check for multimodal content (array takes precedence)
    const hasMultimodalContent = isMessageSent && isMultimodalContent(messageContent?.content);
    const multimodalParts = hasMultimodalContent
        ? (messageContent?.content as Array<MultimodalPart>)
        : [];

    // Legacy text/media detection (used when content is not an array)
    const hasText = Boolean(messageContent?.text);
    const hasMedia = Boolean(
        messageContent?.media
        && Array.isArray(messageContent.media)
        && messageContent.media.length > 0,
    );

    const validMedia = useMemo(() => {
        if (!isMessageSent || !messageContent?.media) return [];
        return (messageContent.media as any[]).filter((m) => m !== undefined);
    }, [isMessageSent, messageContent?.media]);

    const totalBubbles = useMemo(() => {
        if (!isMessageSent) return 1;
        if (hasMultimodalContent) return multimodalParts.length;
        let count = 0;
        if (hasText) count += 1;
        if (hasMedia) count += validMedia.length;
        return count;
    }, [
        isMessageSent,
        hasMultimodalContent,
        multimodalParts.length,
        hasText,
        hasMedia,
        validMedia.length,
    ]);

    return {
        isMessageSent,
        hasMultimodalContent,
        multimodalParts,
        hasText,
        hasMedia,
        validMedia,
        totalBubbles,
    };
};

export default useMessageRenderingMetadata;
